/**
 * Handler for the create_pdp_image_set AI function.
 *
 * Shows a three-path chooser (AI Generate, AI Layered Image, From Template) so
 * the user can decide how to create their PDP images.
 *
 * Accepts either a chat-surface adapter (sidebar) or a Vuex action context
 * ({state, commit, dispatch, ...} already namespaced to creativeStudio); the
 * latter is auto-wrapped for backwards compatibility with the CS dispatcher.
 */
import { resolveAttachmentReferences, searchAndShowProductImages } from '@frontend/store/creative-studio/chat-function-handlers/search-products';
import { createCreativeStudioPdpContextAdapter } from '@frontend/store/creative-studio/chat-function-handlers/cs-pdp-adapter';
import { bucketTemplatesBySubtype, getTypeKeys } from '@frontend/store/creative-studio/template-showcase-tags';
import { attachedImageTypesPresetSource, buildDefaultRowSelections, BUILT_IN_TYPES, makeConversationStaleCheck, reindexRowSelections, resolveProductOverlayUrl, sequenceSlotsToPresetSlots, showLayeredImageSizePicker, TYPE_LABELS } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { extractAsin, extractMarketplaceDomain } from '@frontend/store/creative-studio/amazon-asin';
import { classifyReferenceImages } from '@frontend/store/creative-studio/chat-function-handlers/pdp-reference-generation';
import { briefWidgetFields } from '@frontend/store/creative-studio/brief-widget-fields';

function normalizeAdapter(adapterOrContext) {
  if (adapterOrContext && typeof adapterOrContext.addAndSaveMessage === 'function') {
    return adapterOrContext;
  }
  return createCreativeStudioPdpContextAdapter(adapterOrContext);
}

function attachmentsToProductReferences(attachments = []) {
  return (attachments || [])
    .filter((att) => {
      const fileType = String(att.fileType || att.mime_type || '');
      const url = att.payload || att.url || att.preview;

      return fileType.startsWith('image/') && url;
    })
    .map((att) => ({
      type: 'url',
      value: att.payload || att.url || att.preview,
      display_name: att.name || att.filename || 'Attached product image',
      _source: 'attachment_fallback',
    }));
}

function getLatestUserAttachmentReferences(messages = []) {
  const latestUserMessageWithImages = [...(messages || [])]
    .reverse()
    .find((message) => {
      if (message.role !== 'user') return false;
      if (!Array.isArray(message.attachments) || !message.attachments.length) return false;

      return message.attachments.some((att) => {
        const fileType = String(att.fileType || att.mime_type || '');
        return fileType.startsWith('image/') && att.payload;
      });
    });

  if (!latestUserMessageWithImages) {
    return [];
  }

  return attachmentsToProductReferences(latestUserMessageWithImages.attachments);
}

const getRawAttachmentsFromLatestUserMessage = (messages = []) => {
  const msg = [...(messages || [])].reverse().find((m) => {
    if (m.role !== 'user' || !Array.isArray(m.attachments)) return false;
    return m.attachments.some((a) => {
      const ft = String(a.fileType || a.mime_type || '');
      return ft.startsWith('image/') && (a.payload || a.url || a.preview);
    });
  });
  return msg ? msg.attachments : [];
};

const productAssetToRef = (att) => ({
  type: 'url',
  value: att._resolvedUrl || att.payload || att.url || att.preview,
  display_name: att.name || att.filename || 'Product image',
  _source: 'product_asset',
});

/**
 * Load the data the PDP row picker needs: preset sources (AI-recommended +
 * library sequences), templates bucketed by subtype, and the initial preset id.
 * Shared by the no-reference and reference-seeded flows.
 */
export async function loadPdpPickerData(adapter, productReferences) {
  const aiRecommendedSlots = Object.values(BUILT_IN_TYPES).map((t) => ({ key: t.key, label: t.label }));
  const defaultAiSource = {
    id: 'ai',
    source: 'ai',
    sequenceId: null,
    label: 'AI Recommended',
    count: aiRecommendedSlots.length,
    slots: aiRecommendedSlots,
  };

  let presetSources = [defaultAiSource];
  let templatesBySubtype = {};
  let initialPresetId = 'ai';

  try {
    const productRefs = (productReferences || []).filter((r) => !r._deselected);
    const firstRef = productRefs[0] || {};
    const allRefNames = productRefs.map((r) => r.display_name || r.value).filter(Boolean).join(', ');
    const recommendBody = {
      product_name: allRefNames || adapter.getParsedPrompt()?.parsed_prompt || '',
      product_category: firstRef.category || null,
      asin: firstRef.type === 'asin' ? firstRef.value : null,
      product_description: adapter.getParsedPrompt()?.image_prompt
        || adapter.getParsedPrompt()?.product_description
        || adapter.getParsedPrompt()?.parsed_prompt
        || null,
    };

    const results = await Promise.allSettled([
      axios.get('/sequences'),
      axios.post('/sequences/recommend', recommendBody),
      axios.get('/users/me/chat-preferences'),
      axios.get('/networks/templates'),
    ]);
    const [seqRes, recRes, prefRes, tplRes] = results;

    const sequences = seqRes.status === 'fulfilled' ? (seqRes.value.data || []) : [];
    if (seqRes.status === 'rejected') console.error('Failed to load sequences', seqRes.reason);

    const aiTypes = recRes.status === 'fulfilled' ? ((recRes.value.data && recRes.value.data.types) || []) : [];
    const recommendedSequenceId = recRes.status === 'fulfilled'
      ? ((recRes.value.data && recRes.value.data.recommended_sequence_id) || null)
      : null;
    if (recRes.status === 'rejected') console.error('Failed to load recommendation', recRes.reason);

    const lastPrefs = prefRes.status === 'fulfilled' ? (prefRes.value.data || {}) : {};
    if (prefRes.status === 'rejected') console.error('Failed to load chat preferences', prefRes.reason);

    const allTemplates = tplRes.status === 'fulfilled' ? (tplRes.value.data || []) : [];
    if (tplRes.status === 'rejected') console.error('Failed to load templates', tplRes.reason);

    const pdpTemplates = allTemplates.filter((t) => getTypeKeys(t.tags).includes('pdp'));
    templatesBySubtype = bucketTemplatesBySubtype(pdpTemplates);

    const recommendedKeys = aiTypes.map((t) => t.key);
    if (recommendedKeys.length) {
      presetSources = [{
        id: 'ai',
        source: 'ai',
        sequenceId: null,
        label: 'AI Recommended',
        count: recommendedKeys.length,
        slots: recommendedKeys.map((k) => ({
          key: k,
          label: (BUILT_IN_TYPES[k] && BUILT_IN_TYPES[k].label) || TYPE_LABELS[k] || k,
        })),
      }];
    }
    sequences.forEach((seq) => {
      const slots = sequenceSlotsToPresetSlots(seq);
      if (!slots.length) return;
      presetSources.push({
        id: `library:${seq.id}`,
        source: 'library',
        sequenceId: seq.id,
        label: seq.name,
        count: slots.length,
        slots,
      });
    });

    // Image types attached as chips before sending (Add a reference → Image
    // Types) open the picker on exactly that set — the same treatment an
    // attached sequence gets in showSequencePicker. (POR-14039)
    const attachedTypesSource = attachedImageTypesPresetSource(adapter);
    if (attachedTypesSource) {
      presetSources.unshift(attachedTypesSource);
    }

    if (attachedTypesSource) {
      initialPresetId = attachedTypesSource.id;
    } else if (recommendedSequenceId && presetSources.find((s) => s.id === `library:${recommendedSequenceId}`)) {
      initialPresetId = `library:${recommendedSequenceId}`;
    } else if (lastPrefs.last_sequence_id && presetSources.find((s) => s.id === `library:${lastPrefs.last_sequence_id}`)) {
      initialPresetId = `library:${lastPrefs.last_sequence_id}`;
    } else {
      initialPresetId = 'ai';
    }
  } catch (err) {
    console.error('Failed to load PDP picker data', err);
  }

  return { presetSources, templatesBySubtype, initialPresetId };
}

/**
 * Continue the PDP flow as if no reference images had been provided.
 * - Single-type intent: jump to the size picker.
 * - Plural / multi-type intent: load presets + templates and show the multi-type picker.
 *
 * Used by the main handler and by the `pdp_reference_skip` recovery action.
 */
export async function continueWithoutReferences(adapter, { resolvedTypes, isSet, productReferences }) {
  // POR-13504: this step does a ~30s Amazon scrape + AI type recommendation
  // before appending the row picker. If the user leaves this chat ("New" /
  // opened another conversation) meanwhile, that picker must be discarded, not
  // leaked into the now-active chat.
  const isStaleConversation = makeConversationStaleCheck(adapter);
  const isSingleType = !isSet && (resolvedTypes || []).length === 1;
  if (isSingleType) {
    await showLayeredImageSizePicker(adapter);
    return;
  }

  // Keep the spinner ON until the list is actually on screen — these are slow,
  // image-free backend calls (the AI type recommendation + the product-packshot
  // lookup) and the picker only renders after BOTH plus the message save.
  // Run them CONCURRENTLY (they're independent) instead of back-to-back so the
  // wait is ~the slower of the two, not their sum. (POR-13321 — issues #1 & #3)
  adapter.setProcessing(true);
  try {
    const [pickerData, productImageUrl] = await Promise.all([
      loadPdpPickerData(adapter, productReferences),
      // Product packshot for the Hero row's "Original" (use-as-is) tile + layout
      // creation. (POR-13271) Non-fatal: a failed lookup just leaves it empty.
      resolveProductOverlayUrl(productReferences || [], adapter.getMessages() || []).catch(() => null),
    ]);
    const { presetSources, templatesBySubtype, initialPresetId } = pickerData;

    // Left this chat while the backend calls ran → drop the picker. (POR-13504)
    if (isStaleConversation()) return;

    const initialSource = presetSources.find((s) => s.id === initialPresetId) || presetSources[0];
    // Pass the slots themselves (not just their keys) so a preset's per-slot
    // AI/Upload setting carries into the opening cards. (POR-14020)
    const initialRows = buildDefaultRowSelections(initialSource ? initialSource.slots : []);

    await adapter.addAndSaveMessage({
      role: 'assistant',
      content: '',
      widgets: [{
        type: 'pdp_image_set_picker',
        id: 'pdp_image_set_picker',
        rows: initialRows,
        templatesBySubtype,
        presetSources,
        initialPresetId,
        // A brief that already stated the size pre-selects the picker's inline
        // size row, so those users still just hit Create. (POR-14023)
        initialSize: adapter.getParsedPrompt()?.pdp_brief_size || '',
        productImageUrl,
        productLabel: formatRefLabel(productReferences),
        ...briefWidgetFields(adapter),
      }],
    });
  } finally {
    adapter.setProcessing(false);
  }
}

/**
 * Seed the row picker from a list of reference image URLs: one AI row per
 * reference, each carrying its source thumbnail and an inferred type/label
 * (or 'Other'). Generation later places the target product into each reference.
 */
export async function continueWithReferenceImages(adapter, { referenceImageUrls, productReferences, dedupeByType = true }) {
  // POR-13504: classification + picker-data + packshot lookup run for ~30s before
  // the reference row picker is appended. Discard it if the user left this chat.
  const isStaleConversation = makeConversationStaleCheck(adapter);
  // A fresh set: clear any cross-sell Product 2 from a previous set so this one asks
  // for its own when confirmed. (POR-13612-5-2)
  adapter.setParsedPrompt?.({ pdp_cross_sell_product: null });
  const urls = [...new Set((referenceImageUrls || []).filter(Boolean))];
  if (!urls.length) {
    await continueWithoutReferences(adapter, {
      resolvedTypes: adapter.getParsedPrompt()?.pdp_image_types || [],
      isSet: true,
      productReferences,
    });
    return;
  }

  // Keep the spinner ON until the list renders. Run the slow, independent
  // backend calls CONCURRENTLY: reference classification, the picker-data load,
  // and the product-packshot lookup — instead of resolving the packshot AFTER
  // (which left the spinner off during a ~30s Amazon scrape). (POR-13321 — #1 & #3)
  adapter.setProcessing(true);
  let classified;
  let pickerData;
  let productImageUrl = null;
  try {
    [classified, pickerData, productImageUrl] = await Promise.all([
      classifyReferenceImages(urls, Object.values(BUILT_IN_TYPES)),
      loadPdpPickerData(adapter, productReferences),
      resolveProductOverlayUrl(productReferences || [], adapter.getMessages() || []).catch(() => null),
    ]);
  } catch (err) {
    console.error('Failed to seed reference row picker; continuing without references', err);
    adapter.setProcessing(false);
    await continueWithoutReferences(adapter, {
      resolvedTypes: adapter.getParsedPrompt()?.pdp_image_types || [],
      isSet: true,
      productReferences,
    });
    return;
  }

  // Keep only STANDARD PDP types, one row each. The classifier can (a) map
  // several references to the same type (Hero ×2, Lifestyle ×6) and (b) return
  // non-standard "other" images that aren't a real PDP type and have no
  // generation support — barcodes, size charts, or unlabeled extras that surface
  // as junk rows like "Barcode" / "Reference 20". Drop anything not in
  // BUILT_IN_TYPES and dedupe by type so the set has distinct, generatable image
  // types. Users can add more rows via the picker's "+". (POR-13271)
  const knownTypeKeys = new Set(Object.values(BUILT_IN_TYPES).map((t) => t.key));
  const seenTypes = new Set();
  const dedupedClassified = [];
  classified.forEach((c) => {
    let { type, label } = c;
    if (!knownTypeKeys.has(type)) {
      // ASIN auto-fetch: drop non-standard classifications (barcodes, size
      // charts, and other junk the system pulled). Browse (explicit picks): keep
      // EVERY image the user chose — treat an unrecognized one as a generic
      // Lifestyle scene so the product still composes into it. (POR-13321 —
      // Dave: one row per picked image)
      if (dedupeByType) return;
      type = 'lifestyle';
      label = label || (BUILT_IN_TYPES.lifestyle && BUILT_IN_TYPES.lifestyle.label) || 'Lifestyle';
    }
    // The auto-fetched ASIN flow dedupes by type (the classifier maps several
    // fetched images to one type → Hero×2 junk rows). The Browse flow does NOT:
    // the user explicitly picked each image, so keep one row per pick, in order.
    if (dedupeByType) {
      if (seenTypes.has(type)) return;
      seenTypes.add(type);
    }
    dedupedClassified.push({ ...c, type, label });
  });

  const existingByType = Object.fromEntries(
    (adapter.getParsedPrompt()?.pdp_row_selections || []).map((r) => [r.type, r]),
  );

  const rows = reindexRowSelections(dedupedClassified.map((c) => ({
    type: c.type,
    label: c.label,
    mode: 'ai',
    reference_image_url: c.url,
    productMode: 'integrated',
    template_id: null,
    template_thumb: null,
    template_image_url: null,
    scene: null,
    overlayText: existingByType[c.type]?.overlayText || null,
    brandLogoUrl: existingByType[c.type]?.brandLogoUrl || null,
  })));

  // Left this chat while the backend calls ran → drop the picker. (POR-13504)
  if (isStaleConversation()) return;

  try {
    await adapter.addAndSaveMessage({
      role: 'assistant',
      content: '',
      widgets: [{
        type: 'pdp_image_set_picker',
        id: 'pdp_image_set_picker',
        rows,
        templatesBySubtype: pickerData.templatesBySubtype,
        presetSources: pickerData.presetSources,
        initialPresetId: pickerData.initialPresetId,
        initialSize: adapter.getParsedPrompt()?.pdp_brief_size || '',
        referenceMode: true,
        productImageUrl,
        productLabel: formatRefLabel(productReferences),
        ...briefWidgetFields(adapter),
      }],
    });
  } finally {
    adapter.setProcessing(false);
  }
}

/**
 * Fetch reference product images for the given referenceProductRefs and either
 * seed the reference row picker (CS standalone) or show the reference_images_picker.
 * Shows the recovery widget when no images are found. Optionally searches the
 * target product first (searchTargetFirst) — used by the original handler path.
 */
export async function fetchReferenceImagesAndContinue(adapter, {
  referenceProductRefs,
  productReferences,
  userAttachments = [],
  searchTargetFirst = false,
  useReferenceRowPicker = false,
}) {
  // POR-13504: the reference-image fetch is a ~30s backend call; if the user
  // leaves this chat before it resolves, every downstream picker/recovery append
  // must be discarded instead of leaking into the now-active chat.
  const isStaleConversation = makeConversationStaleCheck(adapter);
  const refLabels = referenceProductRefs.map((r) => r.display_name || r.value).filter(Boolean).join(', ');

  await adapter.addAndSaveMessage({
    role: 'assistant',
    content: `Fetching reference images for <b>${refLabels}</b>...`,
  });
  adapter.setProcessing(true);

  const [targetResult, refResult] = await Promise.allSettled([
    searchTargetFirst
      ? searchAndShowProductImages(productReferences, adapter, { skipSpinner: true })
      : Promise.resolve(true),
    axios.post('/networks/search-product-images', {
      product_references: referenceProductRefs,
      multi: true,
    }),
  ]);

  adapter.setProcessing(false);

  // Left this chat while the fetch ran → drop every downstream append. (POR-13504)
  if (isStaleConversation()) return;

  // Target lookup explicitly returned not-found → it already showed its own
  // recovery message. Stop here; do not stack the reference picker on top.
  if (targetResult.status === 'fulfilled' && targetResult.value === false) {
    return;
  }
  if (targetResult.status === 'rejected') {
    console.warn('Target product search failed', targetResult.reason);
  }

  const fetched = refResult.status === 'fulfilled'
    ? (refResult.value.data?.results || [])
        .filter((r) => r.found && r.images?.length)
        .flatMap((r) => r.images.map((url) => ({
          type: 'url',
          value: url,
          display_name: r.display_name || r.value || '',
          _source: 'asin_reference',
        })))
    : [];

  if (refResult.status === 'rejected') {
    console.warn('Reference product image fetch failed', refResult.reason);
  }

  if (fetched.length > 0) {
    if (useReferenceRowPicker) {
      const refUrls = [
        ...fetched.map((f) => f.value),
        ...userAttachments.map((a) => a.value),
      ];
      // Show ALL fetched reference images (one row per image), NOT a deduped set —
      // we can't know which of the source images the user prefers, so surface them
      // all and let the user remove the ones they don't want. (POR-13612-5)
      await continueWithReferenceImages(adapter, { referenceImageUrls: refUrls, productReferences, dedupeByType: false });
      return;
    }

    const overlayUrl = await resolveProductOverlayUrl(productReferences, adapter.getMessages() || []);
    const baseProduct = overlayUrl || (productReferences.length ? productReferences[0].value : null);

    await adapter.addAndSaveMessage({
      role: 'assistant',
      content: 'Here are the images I found. Remove any you don\'t want to use, then continue:',
      widgets: [{
        type: 'reference_images_picker',
        id: 'pdp_reference_picker',
        images: fetched,
        base_product: baseProduct,
      }],
    });
    return;
  }

  // Reference fetch returned no usable images → explicit recovery widget.
  // Do NOT fall through to the multi-type picker.
  await adapter.addAndSaveMessage({
    role: 'assistant',
    content: `I couldn't find images for <b>${refLabels}</b>. What would you like to do?`,
    widgets: [{
      type: 'action_buttons',
      id: 'pdp_reference_recovery',
      buttons: [
        { label: 'Try a different ASIN',    action: 'pdp_reference_retry',  variant: 'primary' },
        { label: 'Upload images instead',   action: 'pdp_reference_upload', variant: 'secondary' },
        { label: 'Continue without',        action: 'pdp_reference_skip',   variant: 'secondary' },
      ],
    }],
  });
}

/**
 * Human label for the resolved target product, used in the reference-step
 * prompt ("Got <label>. Match a style or generate fresh?"). Falls back to the
 * filename/display_name for url/attachment refs. (POR-13321)
 */
function formatRefLabel(productReferences = []) {
  const refs = productReferences || [];
  const ref = refs.find(
    (r) => r && (r.type === 'asin' || r.type === 'gtin' || r.type === 'salsify' || r.type === 'name' || r.type === 'url')
  ) || refs[0];
  if (!ref) return 'your product';
  const val = ref.value || ref.display_name || '';
  if (ref.type === 'asin') return `ASIN ${val}`;
  if (ref.type === 'gtin') return `GTIN ${val}`;
  if (ref.type === 'salsify') return `Salsify ID ${val}`;
  return ref.display_name || String(val) || 'your product';
}

/**
 * POR-13321: before generating the AI-recommended list, ask whether to match an
 * existing style (reference ASIN / browse) or generate fresh. CS standalone, set
 * path only. The button handlers live in NetworksPageV2.onWidgetAction; the typed
 * reference ASIN is routed by the pendingPdpReference branch in sendMessage.
 */
export async function showPdpReferenceAsk(adapter, { productReferences }) {
  await adapter.addAndSaveMessage({
    role: 'assistant',
    content: `Got <b>${formatRefLabel(productReferences)}</b>. `
      + 'Want me to match an existing style, or generate fresh?',
    widgets: [{
      type: 'action_buttons',
      id: 'pdp_reference_ask',
      buttons: [
        { label: 'ASIN', action: 'pdp_ref_ask_asin', variant: 'light' },
        { label: 'Browse images, layouts or Salsify products', action: 'pdp_ref_ask_browse', variant: 'light' },
        { label: 'Skip — generate fresh', action: 'pdp_reference_skip', variant: 'light' },
      ],
    }],
  });

  // Guess-from-typing: if the user types an ASIN/URL here instead of clicking
  // "ASIN", route it as the reference. Cleared when they click any button or type
  // non-ASIN text. (POR-13612-4)
  adapter.setPendingPdpReferenceAsk?.({ productReferences });
}

/**
 * Parse a typed reference identifier into product references. ASIN/GTIN are
 * detected by pattern; anything else is treated as a product name. Returns []
 * for empty input. (POR-13321)
 */
export function parseTypedReference(text = '') {
  const trimmed = String(text || '').trim();
  if (!trimmed) return [];
  // A bare ASIN or a full Amazon product URL resolves to a single clean ASIN
  // (extracted from /dp/…), instead of scanning for any B+9 token. (POR-13612-2)
  const asinFromInput = extractAsin(trimmed);
  if (asinFromInput) {
    // Keep the URL's marketplace so a non-US reference ASIN is fetched on its own
    // Amazon storefront; null for a bare ASIN. (POR-13612-2)
    return [{
      type: 'asin',
      value: asinFromInput,
      display_name: asinFromInput,
      marketplace: extractMarketplaceDomain(trimmed),
    }];
  }
  const refs = [];
  (trimmed.match(/\bB[0-9A-Z]{9}\b/g) || []).forEach((m) => {
    const v = m.toUpperCase();
    if (!refs.find((r) => r.value === v)) refs.push({ type: 'asin', value: v, display_name: v });
  });
  (trimmed.match(/\b\d{12,14}\b/g) || []).forEach((m) => {
    if (!refs.find((r) => r.value === m)) refs.push({ type: 'gtin', value: m, display_name: m });
  });
  if (refs.length === 0) {
    refs.push({ type: 'name', value: trimmed, display_name: trimmed });
  }
  return refs;
}

export const handleCreatePdpImageSet = async ($message, adapterOrContext) => {
  const adapter = normalizeAdapter(adapterOrContext);
  const useReferenceRowPicker = !!(adapter.supportsReferenceRowPicker && adapter.supportsReferenceRowPicker());

  let args = {};
  try {
    args = typeof $message.function_call.arguments === 'string'
      ? JSON.parse($message.function_call.arguments)
      : $message.function_call.arguments || {};
  } catch (e) {
    // fallback to empty args
  }

  const referenceProductRefs = Array.isArray(args.reference_product_references)
    ? args.reference_product_references.filter((r) => r?.type && r?.value)
    : [];

  // Phrases the user uses to pair a target product with reference products / images.
  // Matched against image_prompt and the latest user message so the reference flow
  // still fires when the AI fails to populate reference_product_references.
  const REFERENCE_INTENT_RE = /reference images?|\bbased on\b|\bin the style of\b|\busing\b[^.]*\bas (?:a )?reference\b|\bfeaturing\b[^.]*\bproduct\b|\bfrom this product\b/i;
  const lastUserContent = (() => {
    const msgs = adapter.getMessages() || [];
    const lastUser = [...msgs].reverse().find((m) => m.role === 'user');
    return lastUser?.content || '';
  })();
  const promptHasReferenceIntent = (args.image_prompt && REFERENCE_INTENT_RE.test(args.image_prompt))
    || (adapter.getParsedPrompt()?.image_prompt && REFERENCE_INTENT_RE.test(adapter.getParsedPrompt().image_prompt))
    || (lastUserContent && REFERENCE_INTENT_RE.test(lastUserContent));

  // Safety net: when prompt is clearly a "based on" request but the AI lumped both
  // products into product_references (a common LLM slip), promote ref #2+ into
  // referenceProductRefs so the reference-images flow can still trigger.
  if (promptHasReferenceIntent && referenceProductRefs.length === 0) {
    const rawProductRefs = Array.isArray(args.product_references)
      ? args.product_references.filter((r) => r?.type && r?.value)
      : [];
    if (rawProductRefs.length >= 2) {
      const promoted = rawProductRefs.slice(1).filter((r) => r.type === 'asin' || r.type === 'gtin' || r.type === 'name' || r.type === 'salsify' || r.type === 'url');
      if (promoted.length) {
        referenceProductRefs.push(...promoted);
        args.product_references = rawProductRefs.slice(0, 1);
      }
    }
  }

  // Stronger safety net: AI sometimes drops the reference ASIN entirely (treats
  // "based on the images in this product B07..." as flavor text). Recover by
  // scanning the user's message and image_prompt for ASIN/GTIN patterns and
  // promoting anything not already in product_references into referenceProductRefs.
  if (promptHasReferenceIntent && referenceProductRefs.length === 0) {
    const haystacks = [lastUserContent, args.image_prompt, adapter.getParsedPrompt()?.image_prompt]
      .filter(Boolean)
      .join(' ');
    const asinMatches = (haystacks.match(/\bB[0-9A-Z]{9}\b/g) || []);
    const gtinMatches = (haystacks.match(/\b\d{12,14}\b/g) || []);
    const existingValues = new Set((args.product_references || []).map((r) => String(r.value).toUpperCase()));
    const promoted = [];
    asinMatches.forEach((m) => {
      if (!existingValues.has(m.toUpperCase()) && !promoted.find((p) => p.value === m)) {
        promoted.push({ type: 'asin', value: m, display_name: m });
      }
    });
    gtinMatches.forEach((m) => {
      if (!existingValues.has(m) && !promoted.find((p) => p.value === m)) {
        promoted.push({ type: 'gtin', value: m, display_name: m });
      }
    });
    if (promoted.length) {
      referenceProductRefs.push(...promoted);
    }
  }

  // Raw message attachments carry asset_type; savedAttachments carry the server-resolved URLs.
  // Merge by index so we can filter by asset_type while still using the real URLs.
  const rawMessageAttachments = getRawAttachmentsFromLatestUserMessage(adapter.getMessages() || []);
  const savedUrls = ($message.savedAttachments || []).map((a) => a.payload || a.url);
  const enrichedAttachments = rawMessageAttachments.map((att, i) => ({
    ...att,
    _resolvedUrl: savedUrls[i] || att.payload || att.url || att.preview,
  }));

  const productAssetAtts = enrichedAttachments.filter((a) => a.asset_type === 'product_asset' && a._resolvedUrl);
  const nonProductAssetImageAtts = enrichedAttachments.filter((a) => {
    if (a.asset_type === 'product_asset') return false;
    const ft = String(a.fileType || a.mime_type || '');
    return ft.startsWith('image/') && a._resolvedUrl;
  });

  // --- Deterministic target/reference roles for "<attached product> based on <named reference>" (POR-12923) ---
  // When the user attaches an image (the product they want the set FOR) and names a
  // reference product AFTER a "based on" / "in the style of" / "using ... as reference"
  // phrase, the attachment is the TARGET and the named identifier is the REFERENCE.
  // The LLM frequently reverses this (puts the post-"based on" ASIN into
  // product_references), so resolve it structurally instead of trusting the args.
  const hasAttachedImage = enrichedAttachments.some((a) => {
    const ft = String(a.fileType || a.mime_type || '');
    return ft.startsWith('image/') && a._resolvedUrl;
  });
  if (promptHasReferenceIntent && hasAttachedImage) {
    const referencePhraseRe = /\bbased on\b|\bin the style of\b|\busing\b[^.]*\bas (?:a )?reference\b|\bfeaturing\b/i;
    let afterPhraseText = '';
    [lastUserContent, args.image_prompt, adapter.getParsedPrompt()?.image_prompt].filter(Boolean).forEach((text) => {
      const idx = String(text).search(referencePhraseRe);
      if (idx >= 0) afterPhraseText += ` ${String(text).slice(idx)}`;
    });
    afterPhraseText = afterPhraseText.toUpperCase();
    if (afterPhraseText) {
      const movable = (args.product_references || []).filter(
        (r) => r && (r.type === 'asin' || r.type === 'gtin' || r.type === 'name' || r.type === 'salsify')
          && r.value && afterPhraseText.includes(String(r.value).toUpperCase()),
      );
      if (movable.length) {
        const movableVals = new Set(movable.map((r) => String(r.value).toUpperCase()));
        movable.forEach((r) => {
          if (!referenceProductRefs.find((x) => String(x.value).toUpperCase() === String(r.value).toUpperCase())) {
            referenceProductRefs.push(r);
          }
        });
        args.product_references = (args.product_references || []).filter(
          (r) => !movableVals.has(String(r.value).toUpperCase()),
        );
      }
    }
  }

  // When the AI already identified an explicit product (ASIN/GTIN/name/salsify),
  // all attachments — including product_asset — become reference images.
  const hasExplicitProductId = (args.product_references || []).some(
    (r) => r?.type === 'asin' || r?.type === 'gtin' || r?.type === 'name' || r?.type === 'salsify'
  );
  // References are named products and the only target signal is an attached image
  // (no named target product) → the attachment is the TARGET; references come from
  // the named product(s), fetched separately. See the role-correction block above.
  const attachmentIsTarget = hasAttachedImage && referenceProductRefs.length > 0 && !hasExplicitProductId;
  const referenceImageAtts = attachmentIsTarget
    ? []
    : (hasExplicitProductId
      ? enrichedAttachments.filter((a) => {
        const ft = String(a.fileType || a.mime_type || '');
        return ft.startsWith('image/') && a._resolvedUrl;
      })
      : nonProductAssetImageAtts);

  const isReferenceImagesRequest = args.use_attachments_as_reference ||
    referenceProductRefs.length > 0 ||
    (productAssetAtts.length > 0 && nonProductAssetImageAtts.length > 0) ||
    promptHasReferenceIntent;

  const productReferences = resolveAttachmentReferences(
    args.product_references || [],
    $message.savedAttachments,
    adapter.getMessages() || [],
    isReferenceImagesRequest
  );

  if (attachmentIsTarget) {
    // The attached image(s) are the target product; references come from the
    // named reference product(s), fetched separately below.
    const targetAtts = enrichedAttachments.filter((a) => {
      const ft = String(a.fileType || a.mime_type || '');
      return ft.startsWith('image/') && a._resolvedUrl;
    });
    if (targetAtts.length) {
      productReferences.length = 0;
      productReferences.push(...targetAtts.map((att) => ({
        type: 'url',
        value: att._resolvedUrl,
        display_name: att.name || att.filename || 'Attached product image',
        _source: 'attachment_target',
      })));
    }
  } else if (!hasExplicitProductId && productAssetAtts.length > 0) {
    productReferences.length = 0;
    productReferences.push(...productAssetAtts.map(productAssetToRef));
  }

  const imageTypes = args.image_types || [];
  // Singular intent ("Create a PDP image") with no specific type → default to a
  // single Hero image so we don't show the set-suggestion widget. Plural
  // intent ("Create a PDP image set") keeps image_types empty and falls back
  // to the default set (Hero + How To + Reason To Believe) later.
  const isSet = args.is_set === true || imageTypes.length > 1;
  const resolvedTypes = imageTypes.length === 0 && !isSet ? ['hero'] : imageTypes;

  // If the user triggered the PDP flow without identifying any product
  // (no ASIN/GTIN/name/url/attachment ref AND no image attached in chat),
  // ask for product info instead of jumping straight into the path picker.
  // Otherwise the AI ends up generating placeholder PDPs for nothing.
  const hasProductRef = productReferences.some(
    (r) => r && (r.type === 'asin' || r.type === 'gtin' || r.type === 'name' || r.type === 'url' || r.type === 'attachment' || r.type === 'salsify')
  );
  const hasUserImageAttachment = (adapter.getMessages() || []).some((m) => {
    if (m.role !== 'user' || !Array.isArray(m.attachments)) return false;
    return m.attachments.some((a) => {
      const fileType = String(a.fileType || a.mime_type || '');
      const url = a.payload || a.url || a.preview;
      return url && (fileType.startsWith('image/') || a.type === 'url' || a.type === 'file');
    });
  });

  if (!hasProductRef && !hasUserImageAttachment) {
    const noun = isSet ? 'PDP image set' : 'PDP image';
    await adapter.addAndSaveMessage({
      role: 'assistant',
      content:
        `What product would you like to create a ${noun} for? ` +
        'You can enter an ASIN, Salsify ID, GTIN, attach a reference image, or describe what you want.',
      widgets: [{
        type: 'action_buttons',
        id: 'pdp_from_brief',
        buttons: [{ label: 'Or start from a brief →', action: 'from_brief_open', variant: 'light' }],
      }],
    });
    return;
  }

  adapter.setParsedPrompt({
    ...adapter.getParsedPrompt(),
    product_references: productReferences,
    pdp_image_types: resolvedTypes,
    pdp_is_set: isSet,
    background_scene: args.background_scene || null,
    image_prompt: args.image_prompt || null,
  });
  await adapter.saveConversationState();

  // Show a visual confirmation of the resolved product image (e.g. "I fetched
  // the hero image for B0...") before the path picker. Skipped silently if no
  // searchable refs (URL/attachment refs are already covered by the adapter).
  const referenceFetchPlanned = isReferenceImagesRequest && referenceProductRefs.length > 0;

  const searchable = productReferences.filter(
    (r) => r.type === 'asin' || r.type === 'gtin' || r.type === 'name' || r.type === 'salsify' || (r.type === 'url' && r.display_name)
  );

  // POR-13321: when the user gave only a target product (no reference) and we're
  // on the set / row-picker path in CS standalone, offer the reference step
  // before the AI-recommended list. This also suppresses the product card below
  // (we go straight to the "Got <product>…" prompt — decision: replace the card).
  const isRowPickerPath = isSet || resolvedTypes.length > 1;
  const askForReference = useReferenceRowPicker && !isReferenceImagesRequest && isRowPickerPath;

  if (searchable.length && !referenceFetchPlanned && !askForReference) {
    const searchOk = await searchAndShowProductImages(productReferences, adapter);
    if (searchOk === false) {
      return;
    }
  }

  let userAttachments = attachmentIsTarget
    ? []
    : (referenceImageAtts.length > 0
      ? referenceImageAtts.map((att) => ({
        type: 'url',
        value: att._resolvedUrl || att.payload || att.url || att.preview,
        display_name: att.name || att.filename || 'Reference image',
        _source: 'attachment_fallback',
      }))
      : getLatestUserAttachmentReferences(adapter.getMessages() || []));

  if (isReferenceImagesRequest) {
    if (referenceProductRefs.length > 0) {
      await fetchReferenceImagesAndContinue(adapter, {
        referenceProductRefs,
        productReferences,
        userAttachments,
        searchTargetFirst: searchable.length > 0 && !attachmentIsTarget,
        useReferenceRowPicker,
      });
      return;
    }

    // No reference ASINs but attachments-as-reference path (existing behavior).
    if (userAttachments.length > 0) {
      if (useReferenceRowPicker) {
        await continueWithReferenceImages(adapter, {
          referenceImageUrls: userAttachments.map((a) => a.value),
          productReferences,
        });
        return;
      }

      const overlayUrl = await resolveProductOverlayUrl(productReferences, adapter.getMessages() || []);
      const baseProduct = overlayUrl || (productReferences.length ? productReferences[0].value : null);

      await adapter.addAndSaveMessage({
        role: 'assistant',
        content: 'Here are the images I found. Remove any you don\'t want to use, then continue:',
        widgets: [{
          type: 'reference_images_picker',
          id: 'pdp_reference_picker',
          images: userAttachments,
          base_product: baseProduct,
        }],
      });
      return;
    }
  }

  if (askForReference) {
    await showPdpReferenceAsk(adapter, { productReferences });
    return;
  }

  await continueWithoutReferences(adapter, { resolvedTypes, isSet, productReferences });
};